import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Sparkles, Heart, ChevronRight, MapPin, Loader2, Lock, Crown, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { UserBadges } from './UserBadges';
import { useAuth, API } from '../App';
import axios from 'axios';

const MatchSuggestions = ({ onLike }) => {
  const { user, token, premiumEnabled } = useAuth();
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [requiresPremium, setRequiresPremium] = useState(false);
  const [premiumDisabled, setPremiumDisabled] = useState(false);

  useEffect(() => {
    const fetchSuggestions = async () => {
      try {
        const response = await axios.get(`${API}/discover/suggestions?limit=5`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setSuggestions(response.data.suggestions || []);
        setIsPremium(response.data.is_premium || false);
        setRequiresPremium(response.data.requires_premium || false);
        setPremiumDisabled(response.data.premium_disabled || false);
      } catch (err) {
        console.error('Failed to fetch suggestions:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) fetchSuggestions();
  }, [token]);

  if (loading) {
    return (
      <div className="bg-card rounded-2xl border border-border p-4 mb-4">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
        </div>
      </div>
    );
  }

  // Show "coming soon" if premium is globally disabled
  if (premiumDisabled || premiumEnabled === false) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-slate-500/10 via-slate-400/10 to-slate-500/10 rounded-2xl border border-slate-500/30 p-5 mb-4"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-slate-500/20 flex items-center justify-center">
            <Clock className="w-5 h-5 text-slate-400" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">AI Match Forslag</h3>
            <p className="text-sm text-muted-foreground">Kommer snart</p>
          </div>
        </div>
        
        <p className="text-sm text-foreground/80">
          Premium-funktioner er midlertidigt deaktiveret mens vi bygger op. 
          Hold øje med opdateringer!
        </p>
      </motion.div>
    );
  }

  // Show premium upsell for non-premium users
  if (requiresPremium && !isPremium) {
    return (
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-amber-500/10 rounded-2xl border border-amber-500/30 p-5 mb-4"
      >
        <div className="flex items-center gap-3 mb-3">
          <div className="w-10 h-10 rounded-full bg-amber-500/20 flex items-center justify-center">
            <Sparkles className="w-5 h-5 text-amber-500" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">AI Match Forslag</h3>
            <p className="text-sm text-muted-foreground">Premium funktion</p>
          </div>
        </div>
        
        <p className="text-sm text-foreground/80 mb-4">
          Få personlige anbefalinger baseret på dine interesser, lokation og kompatibilitet. 
          Se hvem der passer bedst til dig!
        </p>
        
        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            onClick={() => navigate('/premium?trial=true')}
            className="bg-gradient-to-r from-green-500 to-emerald-500 hover:from-green-600 hover:to-emerald-600 text-white rounded-full"
            data-testid="try-premium-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Prøv gratis i 3 dage
          </Button>
          <Button
            onClick={() => navigate('/premium')}
            variant="outline"
            className="rounded-full border-amber-500/50 text-amber-600 hover:bg-amber-500/10"
            data-testid="upgrade-suggestions-btn"
          >
            <Crown className="w-4 h-4 mr-2" />
            Se alle planer
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-3">
          Ingen binding • Annuller når som helst
        </p>
      </motion.div>
    );
  }

  if (suggestions.length === 0) {
    return null;
  }

  const handleViewProfile = (userId) => {
    navigate(`/profile/${userId}`);
  };

  const handleLike = async (userId) => {
    if (onLike) {
      onLike(userId);
    }
    // Remove from suggestions
    setSuggestions(prev => prev.filter(s => s.user.id !== userId));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-r from-primary/5 via-accent/5 to-primary/5 rounded-2xl border border-primary/20 p-4 mb-4"
    >
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div>
          <h3 className="font-semibold text-foreground">Anbefalet til dig</h3>
          <p className="text-xs text-muted-foreground">Baseret på din profil</p>
        </div>
      </div>

      {/* Suggestions List */}
      <div className="space-y-3">
        <AnimatePresence>
          {suggestions.map((suggestion, index) => (
            <motion.div
              key={suggestion.user.id}
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              transition={{ delay: index * 0.1 }}
              className="bg-card rounded-xl border border-border p-3 flex items-center gap-3"
            >
              {/* Photo */}
              <div 
                className="w-14 h-14 rounded-xl overflow-hidden flex-shrink-0 cursor-pointer"
                onClick={() => handleViewProfile(suggestion.user.id)}
              >
                {suggestion.user.photos?.[0] ? (
                  <img
                    src={suggestion.user.photos[0].startsWith('http') ? suggestion.user.photos[0] : `${API.replace('/api', '')}${suggestion.user.photos[0]}`}
                    alt={suggestion.user.name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-muted flex items-center justify-center text-muted-foreground">
                    {suggestion.user.name?.charAt(0)}
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span 
                    className="font-medium text-foreground truncate cursor-pointer hover:text-primary transition-colors"
                    onClick={() => handleViewProfile(suggestion.user.id)}
                  >
                    {suggestion.user.name}
                  </span>
                  {suggestion.user.age && (
                    <span className="text-muted-foreground text-sm">{suggestion.user.age}</span>
                  )}
                  <UserBadges badges={suggestion.badges} size="xs" maxVisible={2} />
                </div>
                
                {/* Match Score / Label */}
                <div className="flex items-center gap-2 mt-1">
                  {isPremium ? (
                    <>
                      <div className="flex items-center gap-1">
                        <div className="h-1.5 w-16 bg-muted rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-primary to-accent rounded-full"
                            style={{ width: `${suggestion.match_score}%` }}
                          />
                        </div>
                        <span className="text-xs font-medium text-primary">{suggestion.match_score}%</span>
                      </div>
                      {suggestion.reasons?.[0] && (
                        <span className="text-xs text-muted-foreground truncate">
                          {suggestion.reasons[0]}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-primary font-medium flex items-center gap-1">
                      <Heart className="w-3 h-3 fill-primary" />
                      {suggestion.match_label || "God match!"}
                    </span>
                  )}
                </div>

                {/* Distance */}
                {suggestion.user.distance_km && (
                  <div className="flex items-center gap-1 mt-1 text-xs text-muted-foreground">
                    <MapPin className="w-3 h-3" />
                    {suggestion.user.distance_km} km væk
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-8 h-8 p-0 rounded-full hover:bg-primary/10 hover:text-primary"
                  onClick={() => handleLike(suggestion.user.id)}
                >
                  <Heart className="w-4 h-4" />
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="w-8 h-8 p-0 rounded-full"
                  onClick={() => handleViewProfile(suggestion.user.id)}
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};

export default MatchSuggestions;
